import { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { useSettings } from "@/lib/useSettings";
import { api } from "@/lib/api";
import { Send, CheckCircle2, AlertCircle, Mail, Phone, MapPin } from "lucide-react";
import SeoHead, { SITE_URL } from "@/components/SeoHead";
import { buildContactPageSchema, buildLocalBusinessSchema } from "@/lib/schema";

const Contact = () => {
  const { lang } = useLang();
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "", website: "" });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setStatus(null);
    try {
      await api.post("/contact", form);
      setStatus("ok");
      setForm({ name: "", email: "", company: "", subject: "", message: "", website: "" });
    } catch {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  };

  const labels = lang === "fr" ? {
    title: "Contacter Dataleo",
    intro: "Une question, un partenariat, un projet IA en Supply Chain ou Opérations à cadrer ? Écrivez-nous, nous répondons sous 48h ouvrées.",
    name: "Nom", email: "Email", company: "Entreprise", subject: "Sujet", message: "Message",
    send: "Envoyer", sending: "Envoi…",
    ok: "Message envoyé. Nous revenons vers vous rapidement.",
    err: "Erreur lors de l'envoi. Réessayez ou écrivez-nous directement.",
  } : {
    title: "Contact Dataleo",
    intro: "Question, partnership, AI project in Supply Chain or Operations to frame? Write to us — we reply within 2 business days.",
    name: "Name", email: "Email", company: "Company", subject: "Subject", message: "Message",
    send: "Send", sending: "Sending…",
    ok: "Message sent. We'll get back to you shortly.",
    err: "Something went wrong. Try again or reach us directly.",
  };

  return (
    <div data-testid="contact-page" className="dl-container py-10 max-w-3xl">
      <SeoHead
        lang={lang}
        path="/contact"
        title={labels.title}
        description={labels.intro}
        jsonLd={[
          buildContactPageSchema({ baseUrl: SITE_URL, settings }),
          buildLocalBusinessSchema({ baseUrl: SITE_URL, settings }),
        ]}
      />
      <div className="border-b border-[#111827] pb-4 mb-6">
        <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878] inline-flex items-center gap-1.5">
          <Mail size={11} /> Contact
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mt-1 text-[#111827]">{labels.title}</h1>
        <p className="mt-3 text-[#4B5563]">{labels.intro}</p>
      </div>

      {/* Visible business info — boosts LocalBusiness/ContactPage signals for crawlers */}
      <address className="not-italic mb-8 grid sm:grid-cols-3 gap-3 text-[13px] text-[#111827]">
        {(settings.address_street || settings.address_city) && (
          <div className="flex items-start gap-2" data-testid="contact-address">
            <MapPin size={14} className="mt-0.5 text-[#00A878] shrink-0" />
            <div>
              <div className="dl-overline text-[#4B5563]">{lang === "fr" ? "Adresse" : "Address"}</div>
              <div>{settings.address_street}</div>
              <div>{[settings.address_postal_code, settings.address_city].filter(Boolean).join(" ")}</div>
              <div>{settings.address_country}</div>
            </div>
          </div>
        )}
        {settings.phone && (
          <div className="flex items-start gap-2" data-testid="contact-phone">
            <Phone size={14} className="mt-0.5 text-[#00A878] shrink-0" />
            <div>
              <div className="dl-overline text-[#4B5563]">{lang === "fr" ? "Téléphone" : "Phone"}</div>
              <a href={`tel:${settings.phone.replace(/\s+/g, "")}`} className="hover:text-[#00A878]">
                {settings.phone}
              </a>
            </div>
          </div>
        )}
        {settings.contact_email && (
          <div className="flex items-start gap-2">
            <Mail size={14} className="mt-0.5 text-[#00A878] shrink-0" />
            <div>
              <div className="dl-overline text-[#4B5563]">Email</div>
              <a href={`mailto:${settings.contact_email}`} className="hover:text-[#00A878]">
                {settings.contact_email}
              </a>
            </div>
          </div>
        )}
      </address>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="flex flex-col gap-1">
            <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{labels.name} *</span>
            <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{labels.email} *</span>
            <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{labels.company}</span>
            <input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{labels.subject}</span>
            <input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]" />
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{labels.message} *</span>
          <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]" />
        </label>

        <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" />

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="contact-submit"
            className="inline-flex items-center gap-2 bg-[#FFD400] hover:bg-[#facc15] disabled:opacity-60 text-[#111827] px-5 py-3 text-sm font-bold"
          >
            <Send size={14} /> {busy ? labels.sending : labels.send}
          </button>
          {status === "ok" && (
            <div data-testid="contact-ok" className="inline-flex items-center gap-2 text-sm text-[#00A878]">
              <CheckCircle2 size={16} /> {labels.ok}
            </div>
          )}
          {status === "error" && (
            <div data-testid="contact-error" className="inline-flex items-center gap-2 text-sm text-red-700">
              <AlertCircle size={16} /> {labels.err}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default Contact;
